import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import AdminShell from './AdminShell.jsx';
import Button from './Button.jsx';
import { PageSpinner } from './Spinner.jsx';
import {
  canUseAdminArea, firstAllowedRoute, hasSection, sectionForRoute,
} from '../lib/permissions.js';

/**
 * Gate for admin routes.
 *
 * Two roles get in: a full `admin`, and an `outlet_manager` limited to the
 * sections an admin granted them. A signed-in user without access gets an
 * explicit 403 rather than a redirect to /login, which would imply their
 * session is broken when the real answer is "this account lacks access".
 *
 * This is a UX affordance, not a security control — the API authorises every
 * admin endpoint independently and filters data to the manager's outlet.
 */
export default function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageSpinner label="Checking your access" />;

  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  if (!canUseAdminArea(user)) {
    return (
      <div className="shell flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
        <span className="eyebrow">403 — Forbidden</span>
        <h1 className="mt-3 font-display text-3xl text-heading">
          This area is for staff accounts
        </h1>
        <p className="mt-3 max-w-md text-sm text-ink-muted">
          You&apos;re signed in as {user.email}, which doesn&apos;t have admin access. If you
          think it should, ask an existing admin to update your role.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button to="/app">Go to my box</Button>
          <Button variant="secondary" to="/">
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  // A manager who lands on a section they don't hold is sent to one they do,
  // rather than shown an error for simply following the default /admin link.
  const section = sectionForRoute(location.pathname);
  if (!hasSection(user, section)) {
    const home = firstAllowedRoute(user);
    if (home && home !== location.pathname) return <Navigate to={home} replace />;
    return (
      <AdminShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
          <span className="eyebrow">No access</span>
          <h1 className="mt-3 font-display text-2xl text-heading">
            You don&apos;t have access to this section
          </h1>
          <p className="mt-3 max-w-md text-sm text-ink-muted">
            Ask an administrator to grant it from Settings → Outlet managers.
          </p>
        </div>
      </AdminShell>
    );
  }

  return <AdminShell>{children ?? <Outlet />}</AdminShell>;
}
